import React, { useState } from 'react';
import { Box, Typography, Button, Container, Link } from '@mui/material';
import { Navigate } from 'react-router-dom';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExploreIcon from '@mui/icons-material/Explore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { isValidIndianMobile, PORTAL_VERIFIED_PHONE_KEY, readStorage, writeStorage } from './portalAuth';
import { getInAppBrowserName } from '../../utils/inAppBrowser';
import App20Logo from '../../components/common/App20Logo';

// Short visible heights (iPhone SE/mini with Safari or in-app browser bars) get a compact layout
// so the whole page fits without scrolling.
const SHORT = '@media (max-height: 700px)';

const SUPPORT_URL = 'https://app20.in/contact';

const font = '"Plus Jakarta Sans", sans-serif';

const isStandalone = () =>
  (window.navigator as Navigator & { standalone?: boolean }).standalone === true ||
  window.matchMedia?.('(display-mode: standalone)').matches;

// Safari doesn't share storage with in-app browsers, so the copied link
// carries the verified phone. TODO: swap for a backend token once OTP is real.
const getVerifiedPhone = (): string | null => {
  const fromUrl = new URLSearchParams(window.location.search).get('phone');
  if (fromUrl && isValidIndianMobile(fromUrl)) {
    writeStorage('local', PORTAL_VERIFIED_PHONE_KEY, fromUrl);
    return fromUrl;
  }
  return readStorage('local', PORTAL_VERIFIED_PHONE_KEY);
};

const StepRow = ({ n, children, last }: { n: number; children: React.ReactNode; last?: boolean }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.1, [SHORT]: { py: 0.6 }, borderBottom: last ? 'none' : '1px solid #E5EAE4' }}>
    <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#E6F4EA', color: '#0F231A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '14px', flexShrink: 0, fontFamily: font }}>
      {n}
    </Box>
    <Box sx={{ color: '#000', fontSize: '14px', [SHORT]: { fontSize: '13px' }, fontFamily: font, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {children}
    </Box>
  </Box>
);

const HomeScreenIllustration = () => (
  <Box component="svg" viewBox="0 0 96 96" fill="none" aria-hidden="true" sx={{ width: 96, height: 96, display: 'block', [SHORT]: { width: 64, height: 64 } }}>
    <circle cx="44" cy="52" r="40" fill="#E6F4EA" />
    <rect x="24" y="14" width="40" height="70" rx="8" fill="#fff" stroke="#0F231A" strokeWidth="4" />
    {[0, 1, 2].map((row) => [0, 1, 2].map((col) => (
      <rect key={`${row}-${col}`} x={31 + col * 10} y={26 + row * 12} width="7" height="7" rx="2" fill={row === 1 && col < 2 ? '#0F231A' : '#BFC7C2'} />
    )))}
    <rect x="31" y="64" width="7" height="7" rx="2" fill="#BFC7C2" />
    <rect x="41" y="64" width="7" height="7" rx="2" fill="#BFC7C2" />
    <circle cx="64" cy="50" r="15" fill="#C9F23C" />
    <path d="M64 43v14M57 50h14" stroke="#0F231A" strokeWidth="3.5" strokeLinecap="round" />
    <path d="M72 18l3-6M78 24l6-3M76 30h6" stroke="#C9F23C" strokeWidth="3" strokeLinecap="round" />
  </Box>
);

export default function PortalHomeScreenPage() {
  const [verifiedPhone] = useState(getVerifiedPhone);
  const inAppBrowser = getInAppBrowserName();
  const [installed] = useState(isStandalone);
  const [linkCopied, setLinkCopied] = useState(false);

  if (!verifiedPhone) return <Navigate to="/portal/login" replace />;

  const appLink = `${window.location.origin}/portal/home-screen?phone=${verifiedPhone}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(appLink);
      setLinkCopied(true);
    } catch {
      window.prompt('Copy this link and open it in Safari:', appLink);
    }
  };

  return (
    <Box sx={{ height: '100vh', '@supports (height: 100dvh)': { height: '100dvh' }, overflow: 'hidden', bgcolor: '#F2F5F1', display: 'flex', flexDirection: 'column' }}>
      {/* Dark Green Top Section (same as the payment page) */}
      <Box sx={{ bgcolor: '#0F231A', borderBottomLeftRadius: '32px', borderBottomRightRadius: '32px', px: 2, pt: 2, pb: 3, [SHORT]: { pt: 1.5, pb: 2 }, color: '#fff' }}>
        <Container maxWidth="sm" sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, [SHORT]: { mb: 1.5 } }}>
            <App20Logo />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: '#C9F23C', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <CheckIcon sx={{ color: '#0F231A', fontSize: 22 }} />
            </Box>
            <Typography sx={{ color: '#fff', fontSize: '32px', [SHORT]: { fontSize: '26px' }, fontWeight: 700, lineHeight: 1.1, fontFamily: '"Moan Slight", sans-serif', letterSpacing: '-1px' }}>
              You're all set!
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ px: 2, pt: 2, [SHORT]: { pt: 1.5 } }}>

        {/* Save to home screen */}
        {installed ? (
          <Box sx={{ bgcolor: '#fff', borderRadius: '24px', p: 2.5, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CheckCircleIcon sx={{ color: '#0F763F', fontSize: 26 }} />
            <Typography sx={{ color: '#000', fontSize: '16px', fontWeight: 800, fontFamily: font }}>App20 is on your home screen</Typography>
          </Box>
        ) : (
          <Box sx={{ bgcolor: '#fff', borderRadius: '24px', p: 2.5, mb: 1, [SHORT]: { p: 2 } }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, [SHORT]: { mb: 1.25 } }}>
              <Box sx={{ flexShrink: 0 }}><HomeScreenIllustration /></Box>
              <Box>
                <Typography sx={{ color: '#0F231A', fontSize: '22px', fontWeight: 800, lineHeight: 1.15, mb: 1, [SHORT]: { fontSize: '19px', mb: 0.5 }, fontFamily: font, letterSpacing: '-0.5px' }}>
                  Save App20 to your home screen
                </Typography>
                <Typography sx={{ color: '#666', fontSize: '14px', [SHORT]: { fontSize: '13px' }, lineHeight: 1.4, fontFamily: font }}>
                  Open App20 from your home screen whenever you're ready to continue.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ bgcolor: '#F4F6F3', borderRadius: '18px', px: 2, py: 0.5, mb: 2, [SHORT]: { mb: 1.25 } }}>
              <StepRow n={1}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Open this page in Safari <ExploreIcon sx={{ color: '#1E88E5', fontSize: 24 }} />
                  </Box>
                  {inAppBrowser && (
                    <Typography sx={{ color: '#666', fontSize: '12px', mt: 0.5, fontFamily: font }}>
                      Tap <MoreHorizIcon sx={{ fontSize: 15, verticalAlign: 'text-bottom' }} /> at the top-right, then <b>Open in external browser</b>
                    </Typography>
                  )}
                </Box>
              </StepRow>
              <StepRow n={2}>
                Tap the Share icon <IosShareIcon sx={{ color: '#1E88E5', fontSize: 24 }} />
              </StepRow>
              <StepRow n={3} last>
                Choose "Add to Home Screen" <AddBoxOutlinedIcon sx={{ color: '#555', fontSize: 24 }} />
              </StepRow>
            </Box>

            <Button
              variant="contained"
              fullWidth
              onClick={handleCopyLink}
              startIcon={linkCopied ? <CheckIcon /> : <ContentCopyIcon />}
              sx={{
                bgcolor: '#0F763F !important',
                color: '#fff',
                py: 1.8,
                [SHORT]: { py: 1.3 },
                borderRadius: '16px',
                fontSize: '16px',
                fontWeight: 800,
                textTransform: 'none',
                boxShadow: 'none',
                fontFamily: font,
                '&:hover': { bgcolor: '#0d6535 !important', boxShadow: 'none' }
              }}
            >
              {linkCopied ? 'Link copied' : 'Copy App20 link'}
            </Button>
            <Typography sx={{ color: '#666', fontSize: '13px', textAlign: 'center', mt: 1.25, [SHORT]: { mt: 0.75 }, fontFamily: font }}>
              You can return using the link you saved.
            </Typography>
          </Box>
        )}
      </Container>

      {/* Support */}
      <Box sx={{ pt: 0.5, pb: 'calc(16px + env(safe-area-inset-bottom))' }}>
        <Container maxWidth="sm" sx={{ px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ flexGrow: 1, borderTop: '1px solid #D5DAD6' }} />
            <Typography sx={{ color: '#444', fontSize: '14px', fontFamily: font, display: 'flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
              <HeadsetMicOutlinedIcon sx={{ fontSize: 18 }} />
              Need help?
              <Link href={SUPPORT_URL} target="_blank" rel="noopener noreferrer" sx={{ color: '#0F763F', fontWeight: 800, textDecorationColor: '#0F763F' }}>
                Get support
              </Link>
            </Typography>
            <Box sx={{ flexGrow: 1, borderTop: '1px solid #D5DAD6' }} />
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
